import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import TimelineItem from "@/components/TimelineItem";
import { Progress } from "@/components/ui/progress";

const CYCLES = [
  { key: "encuentro", label: "Ciclo del Encuentro", description: "El descubrimiento del Dios que sale a nuestro encuentro." },
  { key: "sacramental", label: "Ciclo Sacramental", description: "Los signos del amor de Dios que nos transforman." },
  { key: "franciscano", label: "Ciclo Franciscano", description: "El camino de San Francisco como escuela de vida cristiana." },
];

export default function LevelPage({ level, title, subtitle, accentImage }) {
  const { user } = useAuth();
  const [topics, setTopics] = useState([]);
  const [completed, setCompleted] = useState([]);
  const [cms, setCms] = useState({});

  useEffect(() => {
    api.get(`/topics?level=${level}`).then((r) => setTopics(r.data));
    if (user && user.user_id) {
      api.get(`/progress/me`).then((r) => setCompleted(r.data.completed_topic_ids || [])).catch(() => {});
    }
    api.get("/site-settings").then((r) => {
      const key = level === "primera_comunion" ? "pc" : "cf";
      setCms(r.data?.[key] || {});
    }).catch(() => {});
  }, [level, user]);

  const total = topics.length || 1;
  const done = topics.filter((t) => completed.includes(t.id)).length;
  const pct = Math.round((done / total) * 100);

  const displayTitle = cms.title || title;
  const displaySubtitle = cms.subtitle || subtitle;
  const displayImage = cms.image_url || accentImage;
  const displayParagraph = cms.paragraph;
  const heroBannerStyle = cms.banner_url
    ? { backgroundImage: `url(${cms.banner_url})`, backgroundSize: "cover", backgroundPosition: "center" }
    : undefined;

  return (
    <div data-testid={`level-${level}`}>
      <section className="relative overflow-hidden bg-gradient-to-b from-muted/60 to-background" style={heroBannerStyle}>
        {cms.banner_url && <div className="absolute inset-0 bg-background/80" />}
        <div className="relative container mx-auto px-4 sm:px-6 lg:px-12 py-16 lg:py-24 grid lg:grid-cols-2 gap-10 items-center">
          <div className="animate-fade-up">
            <p className="text-xs tracking-[0.3em] uppercase font-semibold text-secondary mb-4">Ruta formativa 2026</p>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-primary leading-[1.05]" data-testid={`level-title-${level}`}>{displayTitle}</h1>
            <p className="mt-5 font-display text-2xl text-foreground/70 italic" data-testid={`level-subtitle-${level}`}>{displaySubtitle}</p>
            {displayParagraph && (
              <p className="mt-5 text-base leading-relaxed text-foreground/70 max-w-xl" data-testid={`level-paragraph-${level}`}>{displayParagraph}</p>
            )}

            {user && user.user_id && (
              <div className="mt-8 rounded-2xl bg-card border border-border p-6">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold">Tu progreso</p>
                  <p className="text-sm text-primary font-semibold" data-testid="progress-text">{done} / {total} temas</p>
                </div>
                <Progress value={pct} data-testid="progress-bar" />
                <p className="mt-2 text-xs text-foreground/60">{pct}% del camino completado</p>
              </div>
            )}
          </div>
          <div className="relative">
            <div className="aspect-square rounded-[2rem] overflow-hidden bg-gradient-to-br from-primary/15 to-secondary/20 flex items-center justify-center">
              <img src={displayImage} alt="" className="w-2/3 h-2/3 object-contain animate-float" />
            </div>
          </div>
        </div>
      </section>

      <section className="container mx-auto px-4 sm:px-6 lg:px-12 py-16">
        {CYCLES.map((cycle) => {
          const cycleTopics = topics.filter((t) => t.cycle === cycle.key);
          if (!cycleTopics.length) return null;
          return (
            <div key={cycle.key} className="mb-20" data-testid={`cycle-${cycle.key}`}>
              <div className="text-center mb-12">
                <p className="text-xs tracking-[0.3em] uppercase font-semibold text-secondary mb-2">{cycle.label}</p>
                <h2 className="font-display text-3xl md:text-5xl text-primary">{cycle.description}</h2>
              </div>
              <div className="relative">
                <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px timeline-line -translate-x-px" />
                {cycleTopics.map((topic, i) => (
                  <TimelineItem
                    key={topic.id}
                    topic={topic}
                    index={i}
                    completed={completed.includes(topic.id)}
                    unlocked={!topic.locked}
                    level={level}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </section>
    </div>
  );
}
